import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Avatar, Drawer } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import {
  v1Users, v1Profiles, v1Contacts, primaryProfile, v2Users, v2Phases, v1UserToClient, ageFromBirth,
  type V1User, type V1Contact, type V1ContactType, type V1Profile, type PbPhase,
} from '../../lib/v1';
import { PhaseBadge } from '../../components/PbPhaseStepper';
import { AufgabenNotizenTab } from './tabs/AufgabenNotizenTab';
import { KontakteTab } from './tabs/KontakteTab';
import { AnfrageGespraechTab } from '../anfragen/AnfrageGespraechTab';
import { CareParamIconRow, careIconKeysFromProfile } from '../../components/care/CareParamIcons';
import { PhaseRulesBlock } from '../../components/care/PhaseRulesBlock';
import { PhoneLink } from '../../components/PhoneLink';
import { ProtokollTable } from '../protokoll/ProtokollTable';
import { DRAWER_PROTOKOLL_ACTIONS } from './drawerProtokoll';
import { v2Members } from '../../lib/familyAccounts';
import { pbRefFromUser } from '../anfragen/anfragenConstants';
import { WiedervorlageBlock } from '../../components/WiedervorlageBlock';
import type { Client, ClientStatus } from '../../types';

// Tabs des PB-Detail-Drawers.
export type DrawerTab = 'overview' | 'kontakte' | 'protokoll' | 'notizen' | 'gespraechsprotokoll';

const STATUS_TONE: Record<ClientStatus, string> = {
  'Aktiv': 'success', 'Ausstehend': 'warning', 'Beendet': 'neutral',
};

const STATUS_LABEL_KEY: Record<string, string> = {
  'Aktiv': 'pflege.list.status.active',
  'Ausstehend': 'pflege.list.status.pending',
  'Beendet': 'pflege.list.status.closed',
};

export interface ClientDetailProps {
  item: Client;
  raw: V1User | null;
  /** Tab, der beim Öffnen des Drawers aktiv sein soll (Default: overview). */
  initialTab?: DrawerTab;
  onClose: () => void;
  onSaved: (u: V1User) => void;
  onEdit: () => void;
  dark: boolean;
}

type DrawerContactForm = {
  id: number;
  kategorie: Array<number | string>;
  bezeichnung: string;
  telefon: string;
  mobile: string;
};

const contactCategoryIds = (value: V1Contact['kategorie']): Array<number | string> => {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  return [value];
};

const contactLabel = (c: V1Contact, types: V1ContactType[], index: number, tr: (key: string, opts?: Record<string, unknown>) => string): string => {
  const ids = contactCategoryIds(c.kategorie);
  const labels = ids
    .map(id => types.find(type => String(type.id) === String(id))?.name)
    .filter(Boolean) as string[];
  if (labels.length) return labels.join(', ');
  const name = [c.vorname, c.nachname].filter(Boolean).join(' ').trim();
  return name || tr('betreuung.form.kontakte.row', { n: index + 1 });
};

export function ClientDetail({ item, raw, initialTab = 'overview', onClose, onSaved, onEdit, dark }: ClientDetailProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [tab, setTab] = useState<DrawerTab>(initialTab);
  const profile = raw ? primaryProfile(raw) : null;
  const [savingQuick, setSavingQuick] = useState(false);
  const [quickSaved, setQuickSaved] = useState(false);
  const [quickError, setQuickError] = useState<string | null>(null);
  const [quick, setQuick] = useState(() => ({
    vorname: String(profile?.vorname ?? item.name.split(' ')[0] ?? ''),
    nachname: String(profile?.nachname ?? item.name.split(' ').slice(1).join(' ') ?? ''),
    telefon: String(raw?.mobile ?? raw?.telefon ?? profile?.telefon ?? item.phone ?? ''),
    email: String(raw?.email ?? ''),
    // Die Bedingungs-Datumsfelder (Phasen-Daten) werden inline im
    // PhaseRulesBlock gespeichert — bewusst NICHT Teil des Quick-Payloads,
    // damit "Speichern" dortige Änderungen nicht überschreibt.
  }));
  const [contacts, setContacts] = useState<DrawerContactForm[]>([]);
  const [contactTypes, setContactTypes] = useState<V1ContactType[]>([]);

  /**
   * Familienkonto: der Anfrage-Eintrag ist ein PB-User, kann im Familienkonto
   * aber eine Kontaktperson sein (z. B. "Max junior Mustermann"), während der
   * eigentliche Pflegeempfänger ("Max Mustermann") ein anderes Konto-Mitglied
   * ist. Kopf und Übersicht sollen dann dessen Daten zeigen (Name, Alter,
   * Pflegegrad, Adresse, Pflegeparameter) — die Anfrage-Metadaten (Stage/
   * Status, Wiedervorlage, zuständiger Mitarbeiter, Anfrage-ID) bleiben die
   * des geöffneten Anfrage-Users (`item`/`raw`), s. PhaseRulesBlock/
   * WiedervorlageBlock weiter unten, die unverändert an `raw` hängen.
   *
   * `raw.pe_user_id`/`pe_profile_id` kommen vom Backend (PbListController::
   * enrichRows, additiv) — `raw` wird bei /pflege und /anfragen immer aus
   * dieser v2-Liste befüllt, ein Extra-Request auf das Familienkonto ist
   * hier also nicht nötig. Fehlt `pe_user_id` (null/undefined) oder ist der
   * geöffnete User bereits selbst der Pflegeempfänger (der Normalfall),
   * bleibt `peOverride` null und die Anzeige verhält sich wie bisher.
   */
  const [peOverride, setPeOverride] = useState<{ client: Client; profile: V1Profile | null; user: V1User } | null>(null);

  useEffect(() => {
    let alive = true;
    setPeOverride(null);
    if (!raw?.id || raw.pe_user_id == null || raw.pe_user_id === raw.id) return () => { alive = false; };
    v1Users.get(raw.pe_user_id)
      .then(peUser => {
        if (!alive) return;
        const peProfile = primaryProfile(peUser);
        setPeOverride({ client: v1UserToClient(peUser), profile: peProfile, user: peUser });
        // „Schnell bearbeiten" bearbeitet die Personendaten des Pflegeempfängers
        // (nicht der Kontaktperson) — Felder auf dessen Stand setzen.
        setQuick(prev => ({
          ...prev,
          vorname: String(peProfile?.vorname ?? ''),
          nachname: String(peProfile?.nachname ?? ''),
          telefon: String(peUser.mobile ?? peUser.telefon ?? peProfile?.telefon ?? ''),
          email: String(peUser.email ?? ''),
        }));
        setQuickSaved(false);
      })
      .catch(() => { /* Pflegeempfänger optional — Fehler still, Anzeige bleibt beim Anfrage-User */ });
    return () => { alive = false; };
  }, [raw?.id, raw?.pe_user_id]);

  const displayClient = peOverride?.client ?? item;
  const displayProfileForIcons = peOverride?.profile ?? raw?.profiles?.[0];
  // Alter nur mit Geburtsdatum — „0 Jahre" darf nie erscheinen.
  const displayAge = displayClient.age > 0 ? displayClient.age : ageFromBirth(displayClient.birth);

  /**
   * Status-Badge: bei PB-Anfragen die Stage/Phase (Name aus pb_phases), nicht
   * das konto-Mapping (das zeigte „Beendet" für jede Anfrage in Neueingang).
   * „Beendet" nur bei tatsächlich abgebrochener Anfrage (negative Phase, s.
   * AnfrageAbbruch / PbDetailPage). Ohne Phase (kein PB) bleibt das konto-Badge.
   */
  const phaseNum = typeof raw?.phase === 'number' ? raw.phase : (raw?.phase ? Number(raw.phase) : null);
  const isAbgebrochen = phaseNum != null && Number.isFinite(phaseNum) && phaseNum < 0;
  const [phases, setPhases] = useState<PbPhase[]>([]);
  useEffect(() => {
    if (phaseNum == null || phaseNum <= 0) return;
    let alive = true;
    v2Phases.list()
      .then(list => { if (alive) setPhases(list); })
      .catch(() => { /* Fallback: raw.phase_label bzw. hardcodierte Phasen-Keys */ });
    return () => { alive = false; };
  }, [phaseNum]);
  const phaseRow = phaseNum != null ? phases.find(p => p.id === phaseNum) : undefined;
  const phaseLabel = (typeof raw?.phase_label === 'string' && raw.phase_label) ? raw.phase_label : (phaseRow?.title ?? null);

  useEffect(() => {
    let alive = true;
    v1Contacts.types()
      .then(types => { if (alive) setContactTypes(types); })
      .catch(e => { if (alive) setQuickError(e instanceof Error ? e.message : tr('pflege.detail.contact_types_load_error')); });
    return () => { alive = false; };
  }, [tr]);

  useEffect(() => {
    let alive = true;
    if (!raw?.id) return () => { alive = false; };
    v1Contacts.listForUser(raw.id, { perPage: 25 })
      .then(res => {
        if (!alive) return;
        setContacts(res.data.map((c, i) => ({
          id: c.id,
          kategorie: contactCategoryIds(c.kategorie),
          bezeichnung: contactLabel(c, contactTypes, i, tr),
          telefon: String(c.telefon ?? ''),
          mobile: String(c.mobile ?? ''),
        })));
      })
      .catch(e => { if (alive) setQuickError(e instanceof Error ? e.message : tr('betreuung.form.kontakte.load_failed')); });
    return () => { alive = false; };
  }, [raw?.id, contactTypes, tr]);

  const setQuickField = (key: keyof typeof quick, value: string | boolean) => {
    setQuick(prev => ({ ...prev, [key]: value }));
    setQuickSaved(false);
  };
  const setContactField = (id: number, key: keyof DrawerContactForm, value: string | Array<number | string>) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, [key]: value } : c));
    setQuickSaved(false);
  };

  const saveQuick = async () => {
    if (!raw?.id || savingQuick) return;
    setSavingQuick(true);
    setQuickError(null);
    try {
      const contactSaves = contacts.map(c => v1Contacts.update(c.id, {
        kategorie: c.kategorie,
        telefon: c.telefon,
        mobile: c.mobile,
      }));
      if (peOverride) {
        // Personendaten gehören dem Pflegeempfänger: PUT /v2/admin/profiles/{pe_profile_id}
        // (schreibt Profil + zugehörigen User). Anfrage-/Stage-Felder
        // (Mitarbeiter, Wiedervorlage, Bedingungen) bleiben am Anfrage-User `raw`.
        const peProfileId = raw.pe_profile_id ?? peOverride.profile?.id ?? null;
        if (peProfileId == null) throw new Error(tr('pflege.detail.pe_profile_unknown'));
        await v2Members.update(peProfileId, {
          vorname: quick.vorname,
          nachname: quick.nachname,
          email: quick.email || null,
          mobile: quick.telefon || null,
          telefon: quick.telefon || null,
        } as Parameters<typeof v2Members.update>[1]);
        await Promise.all(contactSaves);
        const peName = [quick.vorname, quick.nachname].filter(Boolean).join(' ');
        setPeOverride(prev => prev ? {
          ...prev,
          client: { ...prev.client, name: peName || prev.client.name, phone: quick.telefon },
          profile: prev.profile ? { ...prev.profile, vorname: quick.vorname, nachname: quick.nachname } : prev.profile,
          user: { ...prev.user, email: quick.email, mobile: quick.telefon, telefon: quick.telefon },
        } : prev);
        onSaved({ ...raw, pe_name: peName || raw.pe_name } as V1User);
        setQuickSaved(true);
        return;
      }
      const updatedUser = await v1Users.update(raw.id, {
        mobile: quick.telefon,
        telefon: quick.telefon,
        email: quick.email,
      });
      let updatedProfile = profile;
      if (profile?.id) {
        updatedProfile = await v1Profiles.update(profile.id, {
          vorname: quick.vorname,
          nachname: quick.nachname,
        });
      }
      await Promise.all(contactSaves);
      onSaved({
        ...raw,
        ...updatedUser,
        email: quick.email,
        mobile: quick.telefon,
        telefon: quick.telefon,
        profile: updatedProfile ? { ...updatedProfile, vorname: quick.vorname, nachname: quick.nachname } : updatedUser.profile,
      } as V1User);
      setQuickSaved(true);
    } catch (e) {
      setQuickError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setSavingQuick(false);
    }
  };

  return (
    <Drawer open={true} onClose={onClose} width={560} dark={dark}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 14 }}>
          <Avatar name={displayClient.name} userId={Number((peOverride ? raw?.pe_user_id : null) ?? raw?.id) || null} size={56} dark={dark}/>
          <div>
            {/* ID/Datum bleiben die Anfrage-Metadaten des geöffneten Users,
                auch wenn Name/Alter/Pflegegrad unten den Pflegeempfänger zeigen. */}
            <div style={{ fontSize: 11, color: t.muted, fontWeight: 600 }}>{item.id} · {tr('pflege.detail.since')} {item.since}</div>
            <div style={{ fontSize: 19, fontWeight: 700, color: t.heading, marginTop: 2 }}>{displayClient.name}</div>
            <div style={{ fontSize: 12, color: t.muted, marginTop: 3 }}>
              {[displayAge > 0 ? `${displayAge} ${tr('pflege.detail.years')}` : null, displayClient.gender, displayClient.city]
                .filter(Boolean).join(' · ')}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              {isAbgebrochen
                ? <Badge tone="neutral" dark={dark}>{tr('pflege.list.status.closed')}</Badge>
                : phaseNum != null && phaseNum > 0
                  ? <PhaseBadge phase={phaseNum} phaseLabel={phaseLabel} phaseColor={phaseRow?.color} dark={dark} />
                  : <Badge tone={STATUS_TONE[item.status] as any} dark={dark}>{STATUS_LABEL_KEY[item.status] ? tr(STATUS_LABEL_KEY[item.status]) : item.status}</Badge>}
              <Badge tone="brand" dark={dark}>{tr('pflege.detail.care_level')} {displayClient.pflegegrad}</Badge>
            </div>
            {/* Pflegeparameter auf einen Blick (Kacheln-PDF) */}
            <div style={{ marginTop: 8 }}>
              <CareParamIconRow keys={careIconKeysFromProfile(displayProfileForIcons)} dark={dark} size={17} />
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}>
          <Icon.Close c={t.muted} size={18}/>
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '0 24px', borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
        {([
          { k: 'overview',  l: tr('pflege.detail.tab.overview') },
          { k: 'kontakte',  l: tr('pflege.detail.tab.kontakte') },
          { k: 'gespraechsprotokoll', l: tr('pflege.detail.tab.gespraechsprotokoll') },
          { k: 'protokoll', l: tr('pflege.detail.tab.protokoll') },
          { k: 'notizen',   l: tr('pflege.detail.tab.notizen') },
        ] as const).map(x => (
          <button key={x.k} onClick={() => setTab(x.k)} style={{
            padding: '12px 4px', background: 'transparent', border: 'none',
            borderBottom: tab === x.k ? `2px solid ${BRAND}` : '2px solid transparent',
            color: tab === x.k ? BRAND : t.muted, fontSize: 13, fontWeight: 500,
            cursor: 'pointer', marginRight: 12, fontFamily: 'Outfit, sans-serif',
          }}>{x.l}</button>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
        {tab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <section>
              <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: peOverride ? 4 : 10 }}>{tr('pflege.familienkonto.detail.drawer.quick_edit')}</div>
              {peOverride && (
                <div data-testid="quick-edit-subject" style={{ fontSize: 11, color: t.muted, marginBottom: 10 }}>
                  {tr('pflege.familienkonto.detail.role_care_recipient')}: <strong style={{ fontWeight: 600, color: t.text }}>{peOverride.client.name}</strong>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <QuickField dark={dark} label={tr('pflege.form.field.vorname')} value={quick.vorname} onChange={v => setQuickField('vorname', v)} />
                <QuickField dark={dark} label={tr('pflege.form.field.nachname')} value={quick.nachname} onChange={v => setQuickField('nachname', v)} />
                <QuickField
                  dark={dark} label={tr('pflege.detail.quick_edit.telefonnummer')}
                  value={quick.telefon} onChange={v => setQuickField('telefon', v)}
                  /* Anwählbar direkt unter dem Eingabefeld — das Feld selbst
                     muss editierbar bleiben, ein <a> kann es nicht sein. */
                  action={<PhoneLink number={quick.telefon} dark={dark} showIcon />}
                />
                <QuickField dark={dark} label={tr('pflege.form.field.email')} value={quick.email} onChange={v => setQuickField('email', v)} type="email" />
              </div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {contacts.map((c, i) => (
                  <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: t.muted }}>
                      {tr('pflege.detail.quick_edit.contact_kategorie', { n: i + 1 })}
                      <select
                        aria-label={tr('pflege.detail.quick_edit.contact_kategorie', { n: i + 1 })}
                        value={String(c.kategorie[0] ?? '')}
                        onChange={e => setContactField(c.id, 'kategorie', e.target.value ? [Number(e.target.value)] : [])}
                        style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px', background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', fontSize: 13 }}
                      >
                        <option value="">—</option>
                        {contactTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                      </select>
                    </label>
                    <QuickField
                      dark={dark} label={tr('pflege.detail.quick_edit.contact_telefonnummer', { n: i + 1 })}
                      value={c.telefon} onChange={v => setContactField(c.id, 'telefon', v)}
                      action={<PhoneLink number={c.telefon} dark={dark} showIcon />}
                    />
                  </div>
                ))}
              </div>
            </section>
            {/* Bedingungen der PB Stages (Admin-Einstellungen) — live ausgewertet,
                Felder direkt ausfüllbar; Saves fließen in den Parent-State zurück.
                Ersetzt Batch-12b "Phasen-Daten" + PhaseRuleFields (Duplikat). */}
            <PhaseRulesBlock dark={dark} raw={raw} onSaved={u => onSaved?.(u)} />
            {/* Batch-13 — Wiedervorlage: eigener, unabhängig speichernder Block
                (mirrors PhaseRulesBlock) — nur für PB (user_role=2). */}
            <WiedervorlageBlock dark={dark} raw={raw} onSaved={u => onSaved?.(u)} />
            {/* Speichern für "Schnell bearbeiten" — unterhalb der Bedingungen */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button variant="primary" size="sm" dark={dark} onClick={saveQuick} disabled={!raw?.id || savingQuick} icon={<Icon.Check c="#fff" />}>
                {savingQuick ? tr('settings.bk_stages.saving') : tr('common.action.save')}
              </Button>
              {quickSaved && <span style={{ fontSize: 12, color: '#039855', fontWeight: 600 }}>{tr('settings.wiedervorlage_max.saved')}</span>}
              {quickError && <span style={{ fontSize: 12, color: '#b42318', fontWeight: 600 }}>{quickError}</span>}
            </div>
            {/* Der frühere „Kontakt"-Block (Adresse/Telefon/Notfall/Geburtstag
                als Nur-Lese-Zellen) ist entfallen: die Felder stehen editierbar
                im Schnell-bearbeiten-Raster bzw. im Reiter „Kontakte". */}
            {/* Batch-12b: Abschnitte „Betreuung" und „Notizen" aus der Übersicht
                entfernt — Notizen haben weiterhin ihren eigenen Reiter. */}
          </div>
        )}
        {tab === 'kontakte' && <KontakteTab accountId={raw ? pbRefFromUser(raw).accountId : null} excludeUserId={raw?.id} dark={dark} />}
        {tab === 'gespraechsprotokoll' && (
          raw?.id
            ? (
              /* Anrufen + Gespräche. Bei Familienkonto ist der Pflegeempfänger
                 (peOverride) die Kontaktperson, wie im Schnell-bearbeiten-Raster. */
              <AnfrageGespraechTab
                contact={peOverride?.user ?? raw}
                phone={quick.telefon}
                dark={dark}
              />
            )
            : <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('pflege.detail.protokoll_unavailable')}</div>
        )}
        {tab === 'protokoll' && (
          raw?.id
            ? (
              /* Nur E-Mail- und Anruf-Aktivitäten: wer hat wann angerufen und
                 wurde der Kunde erreicht (3cx_call_<status>). Ein ungefilterter
                 Protokoll-Dump würde die Spur in Routine-Requests ersäufen. */
              <ProtokollTable
                dark={dark}
                compact
                fixedQuery={{ user_id: raw.id, action: DRAWER_PROTOKOLL_ACTIONS }}
                hiddenColumns={['route', 'context']}
              />
            )
            : <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('pflege.detail.protokoll_unavailable')}</div>
        )}
        {tab === 'notizen' && (
          raw?.id
            ? <AufgabenNotizenTab user={raw} dark={dark} only="notices" />
            : <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('pflege.detail.notes_unavailable')}</div>
        )}
      </div>

      <div style={{ padding: 14, borderTop: `1px solid ${t.border}`, display: 'flex', gap: 8, background: t.cardMuted }}>
        <Button variant="primary" dark={dark} style={{ flex: 1, justifyContent: 'center' }} icon={<Icon.Edit c="#fff"/>} onClick={onEdit}>
          {tr('common.action.edit')}
        </Button>
      </div>
    </Drawer>
  );
}

function QuickField({
  dark,
  label,
  value,
  onChange,
  type = 'text',
  action,
}: {
  dark: boolean;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  /**
   * Zusatz unter dem Eingabefeld — für Telefonnummern der anwählbare
   * <PhoneLink>. Das Feld selbst bleibt ein Input (es muss editierbar sein),
   * der Link steht daneben.
   */
  action?: React.ReactNode;
}) {
  const t = getThemeColors(dark);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: t.muted }}>
      {label}
      <input
        aria-label={label}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 10px', background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', fontSize: 13 }}
      />
      {action && value ? <span style={{ fontSize: 12 }}>{action}</span> : null}
    </label>
  );
}
